import { _ as __assign, c as __read, d as __rest } from './chunks/tslib.es6-ebb56484.js';
import * as React from 'react';
import React__default, { forwardRef, useState, useEffect } from 'react';
import { useIsMobile, useStyles, useBoolean } from '@sravni/react-utils';
import { FormControl, TextInput } from '@sravni/react-design-system';
import { S as Search$1 } from './chunks/Search-dbda2780.js';

/**
 * @description ArrowLeft icon component.
 *
 * @component
 * @example
 * ```jsx
 * import { ArrowLeft } from '@sravni/design-system-icons'
 *
 * <ArrowLeft />
 * ```
 */
var ArrowLeft = React.forwardRef(function (props, ref) { return (React.createElement("svg", __assign({ ref: ref, "data-qa": "ArrowLeft", viewBox: "0 0 20 20", fill: "none", xmlns: "http://www.w3.org/2000/svg" }, props),
    React.createElement("path", { d: "m13 4-6 6 6 6", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }))); });

/**
 * @description Cross icon component.
 *
 * @component
 * @example
 * ```jsx
 * import { Cross } from '@sravni/design-system-icons'
 *
 * <Cross />
 * ```
 */
var Cross = React.forwardRef(function (props, ref) { return (React.createElement("svg", __assign({ ref: ref, "data-qa": "Cross", viewBox: "0 0 20 20", fill: "none", xmlns: "http://www.w3.org/2000/svg" }, props),
    React.createElement("g", { clipPath: "url(#Cross__a)" },
        React.createElement("path", { d: "m10 10-4.95 4.95m0-9.9L10 10 5.05 5.05ZM10 10l4.95 4.95L10 10Zm0 0 4.95-4.95L10 10Z", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }),
        React.createElement("path", { d: "M79-22H-51a5 5 0 0 0-5 5v54a5 5 0 0 0 5 5H79a5 5 0 0 0 5-5v-54a5 5 0 0 0-5-5Z", stroke: "currentColor", strokeDasharray: "10 5" })),
    React.createElement("defs", null,
        React.createElement("clipPath", { id: "Cross__a" },
            React.createElement("path", { fill: "currentColor", d: "M0 0h20v20H0z" }))))); });

const LEFT = "Left";
const RIGHT = "Right";
const UP = "Up";
const DOWN = "Down";

/* global document */
const defaultProps = {
    delta: 10,
    preventScrollOnSwipe: false,
    rotationAngle: 0,
    trackMouse: false,
    trackTouch: true,
    swipeDuration: Infinity,
    touchEventOptions: { passive: true },
};
const initialState = {
    first: true,
    initial: [0, 0],
    start: 0,
    swiping: false,
    xy: [0, 0],
};
const mouseMove = "mousemove";
const mouseUp = "mouseup";
const touchEnd = "touchend";
const touchMove = "touchmove";
const touchStart = "touchstart";
function getDirection(absX, absY, deltaX, deltaY) {
    if (absX > absY) {
        if (deltaX > 0) {
            return RIGHT;
        }
        return LEFT;
    }
    else if (deltaY > 0) {
        return DOWN;
    }
    return UP;
}
function rotateXYByAngle(pos, angle) {
    if (angle === 0)
        return pos;
    const angleInRadians = (Math.PI / 180) * angle;
    const x = pos[0] * Math.cos(angleInRadians) + pos[1] * Math.sin(angleInRadians);
    const y = pos[1] * Math.cos(angleInRadians) - pos[0] * Math.sin(angleInRadians);
    return [x, y];
}
function getHandlers(set, handlerProps) {
    const onStart = (event) => {
        const isTouch = "touches" in event;
        // if more than a single touch don't track, for now...
        if (isTouch && event.touches.length > 1)
            return;
        set((state, props) => {
            // setup mouse listeners on document to track swipe since swipe can leave container
            if (props.trackMouse && !isTouch) {
                document.addEventListener(mouseMove, onMove);
                document.addEventListener(mouseUp, onUp);
            }
            const { clientX, clientY } = isTouch ? event.touches[0] : event;
            const xy = rotateXYByAngle([clientX, clientY], props.rotationAngle);
            props.onTouchStartOrOnMouseDown &&
                props.onTouchStartOrOnMouseDown({ event });
            return Object.assign(Object.assign(Object.assign({}, state), initialState), { initial: xy.slice(), xy, start: event.timeStamp || 0 });
        });
    };
    const onMove = (event) => {
        set((state, props) => {
            const isTouch = "touches" in event;
            // Discount a swipe if additional touches are present after
            // a swipe has started.
            if (isTouch && event.touches.length > 1) {
                return state;
            }
            // if swipe has exceeded duration stop tracking
            if (event.timeStamp - state.start > props.swipeDuration) {
                return state.swiping ? Object.assign(Object.assign({}, state), { swiping: false }) : state;
            }
            const { clientX, clientY } = isTouch ? event.touches[0] : event;
            const [x, y] = rotateXYByAngle([clientX, clientY], props.rotationAngle);
            const deltaX = x - state.xy[0];
            const deltaY = y - state.xy[1];
            const absX = Math.abs(deltaX);
            const absY = Math.abs(deltaY);
            const time = (event.timeStamp || 0) - state.start;
            const velocity = Math.sqrt(absX * absX + absY * absY) / (time || 1);
            const vxvy = [deltaX / (time || 1), deltaY / (time || 1)];
            const dir = getDirection(absX, absY, deltaX, deltaY);
            // if swipe is under delta and we have not started to track a swipe: skip update
            const delta = typeof props.delta === "number"
                ? props.delta
                : props.delta[dir.toLowerCase()] ||
                    defaultProps.delta;
            if (absX < delta && absY < delta && !state.swiping)
                return state;
            const eventData = {
                absX,
                absY,
                deltaX,
                deltaY,
                dir,
                event,
                first: state.first,
                initial: state.initial,
                velocity,
                vxvy,
            };
            // call onSwipeStart if present and is first swipe event
            eventData.first && props.onSwipeStart && props.onSwipeStart(eventData);
            // call onSwiping if present
            props.onSwiping && props.onSwiping(eventData);
            // track if a swipe is cancelable (handler for swiping or swiped(dir) exists)
            // so we can call preventDefault if needed
            let cancelablePageSwipe = false;
            if (props.onSwiping ||
                props.onSwiped ||
                props[`onSwiped${dir}`]) {
                cancelablePageSwipe = true;
            }
            if (cancelablePageSwipe &&
                props.preventScrollOnSwipe &&
                props.trackTouch &&
                event.cancelable) {
                event.preventDefault();
            }
            return Object.assign(Object.assign({}, state), { 
                // first is now always false
                first: false, eventData, swiping: true });
        });
    };
    const onEnd = (event) => {
        set((state, props) => {
            let eventData;
            if (state.swiping && state.eventData) {
                // if swipe is less than duration fire swiped callbacks
                if (event.timeStamp - state.start < props.swipeDuration) {
                    eventData = Object.assign(Object.assign({}, state.eventData), { event });
                    props.onSwiped && props.onSwiped(eventData);
                    const onSwipedDir = props[`onSwiped${eventData.dir}`];
                    onSwipedDir && onSwipedDir(eventData);
                }
            }
            else {
                props.onTap && props.onTap({ event });
            }
            props.onTouchEndOrOnMouseUp && props.onTouchEndOrOnMouseUp({ event });
            return Object.assign(Object.assign(Object.assign({}, state), initialState), { eventData });
        });
    };
    const cleanUpMouse = () => {
        // safe to just call removeEventListener
        document.removeEventListener(mouseMove, onMove);
        document.removeEventListener(mouseUp, onUp);
    };
    const onUp = (e) => {
        cleanUpMouse();
        onEnd(e);
    };
    /**
     * The value of passive on touchMove depends on `preventScrollOnSwipe`:
     * - true => { passive: false }
     * - false => { passive: true } // Default
     *
     * NOTE: When preventScrollOnSwipe is true, we attempt to call preventDefault to prevent scroll.
     *
     * props.touchEventOptions can also be set for all touch event listeners,
     * but for `touchmove` specifically when `preventScrollOnSwipe` it will
     * supersede and force passive to false.
     *
     */
    const attachTouch = (el, props) => {
        let cleanup = () => { };
        if (el && el.addEventListener) {
            const baseOptions = Object.assign(Object.assign({}, defaultProps.touchEventOptions), props.touchEventOptions);
            // attach touch event listeners and handlers
            const tls = [
                [touchStart, onStart, baseOptions],
                // preventScrollOnSwipe option supersedes touchEventOptions.passive
                [
                    touchMove,
                    onMove,
                    Object.assign(Object.assign({}, baseOptions), (props.preventScrollOnSwipe ? { passive: false } : {})),
                ],
                [touchEnd, onEnd, baseOptions],
            ];
            tls.forEach(([e, h, o]) => el.addEventListener(e, h, o));
            // return properly scoped cleanup method for removing listeners, options not required
            cleanup = () => tls.forEach(([e, h]) => el.removeEventListener(e, h));
        }
        return cleanup;
    };
    const onRef = (el) => {
        // "inline" ref functions are called twice on render, once with null then again with DOM element
        // ignore null here
        if (el === null)
            return;
        set((state, props) => {
            // if the same DOM el as previous just return state
            if (state.el === el)
                return state;
            const addState = {};
            // if new DOM el clean up old DOM and reset cleanUpTouch
            if (state.el && state.el !== el && state.cleanUpTouch) {
                state.cleanUpTouch();
                addState.cleanUpTouch = void 0;
            }
            // only attach if we want to track touch
            if (props.trackTouch && el) {
                addState.cleanUpTouch = attachTouch(el, props);
            }
            // store event attached DOM el for comparison, clean up, and re-attachment
            return Object.assign(Object.assign(Object.assign({}, state), { el }), addState);
        });
    };
    // set ref callback to attach touch event listeners
    const output = {
        ref: onRef,
    };
    // if track mouse attach mouse down listener
    if (handlerProps.trackMouse) {
        output.onMouseDown = onStart;
    }
    return [output, attachTouch];
}
function updateTransientState(state, props, previousProps, attachTouch) {
    // if trackTouch is off or there is no el, then remove handlers if necessary and exit
    if (!props.trackTouch || !state.el) {
        if (state.cleanUpTouch) {
            state.cleanUpTouch();
        }
        return Object.assign(Object.assign({}, state), { cleanUpTouch: undefined });
    }
    // trackTouch is on, so if there are no handlers attached, attach them and exit
    if (!state.cleanUpTouch) {
        return Object.assign(Object.assign({}, state), { cleanUpTouch: attachTouch(state.el, props) });
    }
    // trackTouch is on and handlers are already attached, so if preventScrollOnSwipe changes value,
    // remove and reattach handlers (this is required to update the passive option when attaching
    // the handlers)
    if (props.preventScrollOnSwipe !== previousProps.preventScrollOnSwipe ||
        props.touchEventOptions.passive !== previousProps.touchEventOptions.passive) {
        state.cleanUpTouch();
        return Object.assign(Object.assign({}, state), { cleanUpTouch: attachTouch(state.el, props) });
    }
    return state;
}
function useSwipeable(options) {
    const { trackMouse } = options;
    const transientState = React.useRef(Object.assign({}, initialState));
    const transientProps = React.useRef(Object.assign({}, defaultProps));
    // track previous rendered props
    const previousProps = React.useRef(Object.assign({}, transientProps.current));
    previousProps.current = Object.assign({}, transientProps.current);
    // update current render props & defaults
    transientProps.current = Object.assign(Object.assign({}, defaultProps), options);
    // Force defaults for config properties
    let defaultKey;
    for (defaultKey in defaultProps) {
        if (transientProps.current[defaultKey] === void 0) {
            transientProps.current[defaultKey] = defaultProps[defaultKey];
        }
    }
    const [handlers, attachTouch] = React.useMemo(() => getHandlers((stateSetter) => (transientState.current = stateSetter(transientState.current, transientProps.current)), { trackMouse }), [trackMouse]);
    transientState.current = updateTransientState(transientState.current, transientProps.current, previousProps.current, attachTouch);
    return handlers;
}

var MAX_SEARCH_INPUT = 100;

var modules_b6874b2b = {"searchWrapper":"_lp0vpo","searchInput":"_1rl6nfq","deleteButton":"_1ee38mm","searchWrapperTop":"_mk369v","backButton":"_dao8u4","searchWrapperRubricMenu":"_14z9wqd","searchWrapperMenu":"_1aah5z"};
import "./assets/Search.css";

var SearchInput$1 = forwardRef(function (_a, forwardedRef) {
    var onBlur = _a.onBlur, onBackClick = _a.onBackClick, isTopBlock = _a.isTopBlock, isTopBlockVisible = _a.isTopBlockVisible, isRubricMenu = _a.isRubricMenu, _b = _a.placeholder, placeholder = _b === void 0 ? 'Поиск' : _b, onSearch = _a.onSearch, onFocus = _a.onFocus, onKeyDown = _a.onKeyDown, isAutofocus = _a.isAutofocus, className = _a.className;
    var isMobile = useIsMobile();
    var cx = useStyles(modules_b6874b2b);
    var isMenuBlock = !isTopBlock;
    var _c = __read(useState(''), 2), text = _c[0], setText = _c[1];
    var _d = __read(useBoolean(isAutofocus), 2), isSearchActive = _d[0], setSearchActive = _d[1];
    var invalidText = text.length > MAX_SEARCH_INPUT;
    useEffect(function () {
        if (!isTopBlockVisible)
            setText('');
    }, [isTopBlockVisible]);
    var handleTextChange = function (e) {
        setText(e.currentTarget.value);
    };
    var handleDeleteText = function () {
        onBlur && onBlur();
        setText('');
    };
    var handleKeyDown = function (event) {
        if (!invalidText && event.key === 'Enter') {
            onKeyDown && onKeyDown(event);
            onSearch(text);
        }
    };
    var handleBlur = function () {
        onBlur && onBlur();
        setText('');
        setSearchActive.off();
    };
    var handleBackClick = function (e) {
        e.stopPropagation();
        onBackClick && onBackClick();
    };
    var handleFocus = function () {
        onFocus && onFocus();
        setSearchActive.on();
    };
    var isShowControls = !isTopBlock || (isTopBlockVisible && isSearchActive);
    return (React__default.createElement("div", { className: cx('searchWrapper', isTopBlock && 'searchWrapperTop', isRubricMenu && 'searchWrapperRubricMenu', isMenuBlock && !isRubricMenu && 'searchWrapperMenu', className) },
        React__default.createElement(FormControl, { invalid: invalidText },
            React__default.createElement(TextInput, { className: modules_b6874b2b.searchInput, placeholder: placeholder, autoFocus: isAutofocus === undefined ? !isMobile : isAutofocus, value: text, onChange: handleTextChange, onBlur: handleBlur, onKeyDown: handleKeyDown, icon: isShowControls && !isRubricMenu ? React__default.createElement(Cross, null) : null, leftIcon: isShowControls ? isTopBlock ? React__default.createElement(ArrowLeft, null) : React__default.createElement(Search$1, null) : null, ref: forwardedRef, onFocus: handleFocus }),
            invalidText && React__default.createElement(FormControl.HelperText, null,
                "\u041C\u0430\u043A\u0441\u0438\u043C\u0443\u043C ",
                MAX_SEARCH_INPUT,
                " \u0441\u0438\u043C\u0432\u043E\u043B\u043E\u0432")),
        isTopBlockVisible && React__default.createElement("div", { className: modules_b6874b2b.backButton, onClick: handleBackClick }),
        isShowControls && !isRubricMenu && React__default.createElement("div", { className: modules_b6874b2b.deleteButton, onClick: handleDeleteText })));
});
SearchInput$1.displayName = 'SearchInput';

var SearchBlock$1 = forwardRef(function (_a, forwardedRef) {
    var isTopBlock = _a.isTopBlock, isTopBlockVisible = _a.isTopBlockVisible, toggleOverlay = _a.toggleOverlay, toogleRubricMenu = _a.toogleRubricMenu, isRubricMenu = _a.isRubricMenu, onBlur = _a.onBlur, onSearch = _a.onSearch, onKeyDown = _a.onKeyDown, placeholder = _a.placeholder, isAutofocus = _a.isAutofocus, className = _a.className;
    var handleFocus = function () {
        toggleOverlay && toggleOverlay('on');
    };
    var handleBlur = function () {
        onBlur && onBlur();
        toggleOverlay && toggleOverlay('off');
    };
    var handleBackClick = function () {
        toggleOverlay && toggleOverlay('off');
    };
    var handleSearch = function (text) {
        onSearch(text);
        toggleOverlay && toggleOverlay('off');
    };
    var handleKeyDown = function (event) {
        onKeyDown && onKeyDown(event);
        toogleRubricMenu && toogleRubricMenu('off');
    };
    return (React__default.createElement(SearchInput$1, __assign({}, (isTopBlock ? { isTopBlockVisible: isTopBlockVisible, isTopBlock: isTopBlock } : { isTopBlock: isTopBlock }), (isRubricMenu && { isRubricMenu: isRubricMenu }), { onBlur: handleBlur, onBackClick: handleBackClick, onSearch: handleSearch, onFocus: handleFocus, onKeyDown: function (event) { return handleKeyDown(event); }, ref: forwardedRef, placeholder: placeholder, isAutofocus: isAutofocus, className: className })));
});
SearchBlock$1.displayName = 'SearchBlock';

var modules_6a965cfa = {"topSearch":"_hk7aa5"};
/*SRAVNI__src/components/Search/src/components/TopSearch/top-search.module.scss__SRAVNI*/;

var TopSearch$1 = function (_a) {
    var toggleOverlay = _a.toggleOverlay, rest = __rest(_a, ["toggleOverlay"]);
    var _b = __read(useBoolean(false), 2), isTopBlockVisible = _b[0], setTopBlockVisible = _b[1];
    var cx = useStyles(modules_6a965cfa);
    var ref = useSwipeable({
        onSwiping: function (eventData) {
            if (window.scrollY > 100)
                return;
            if (eventData.dir === 'Down') {
                setTopBlockVisible.on();
            }
            else if (eventData.dir === 'Up') {
                setTopBlockVisible.off();
                toggleOverlay && toggleOverlay('off');
            }
        }
    }).ref;
    useEffect(function () {
        var body = document.getElementsByTagName('body')[0];
        ref(body);
        return function () { return ref(null); };
    });
    return (React__default.createElement("div", { className: cx('topSearch'), style: { maxHeight: "".concat(isTopBlockVisible ? 100 : 0, "px") } },
        React__default.createElement(SearchBlock$1, __assign({ isTopBlock: true, toggleOverlay: toggleOverlay, isAutofocus: false, isTopBlockVisible: isTopBlockVisible }, rest))));
};
TopSearch$1.displayName = 'TopSearch';

var RubricMenuSearch = function (_a) {
    var toogleRubricMenu = _a.toogleRubricMenu, rest = __rest(_a, ["toogleRubricMenu"]);
    var handleKeyDown = function (event) {
        if (event.key === 'Enter') {
            toogleRubricMenu && toogleRubricMenu('off');
        }
    };
    return (React__default.createElement(SearchBlock$1, __assign({ onKeyDown: function (event) { return handleKeyDown(event); }, toogleRubricMenu: toogleRubricMenu, isRubricMenu: true, isTopBlock: false, isAutofocus: false }, rest)));
};
RubricMenuSearch.displayName = 'RubricMenuSearch';

export { RubricMenuSearch, SearchBlock$1 as SearchBlock, TopSearch$1 as TopSearch };
